import React from 'react';
import { Info, CheckCircle2, AlertTriangle, AlertCircle, X } from 'lucide-react';

export type AlertType = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps {
  type?: AlertType;
  title?: string;
  children: React.ReactNode;
  onClose?: () => void;
  action?: React.ReactNode;
  className?: string;
}

const alertStyles: Record<AlertType, { bg: string; border: string; text: string; icon: React.ReactNode }> = {
  info: {
    bg: 'bg-[#E8FAF9]',
    border: 'border-[#56E8E1]/50',
    text: 'text-[#1b8e88]',
    icon: <Info className="w-5 h-5 text-[#1fb4ac] shrink-0" />,
  },
  success: {
    bg: 'bg-[#EAF8F1]',
    border: 'border-[#20A66A]/40',
    text: 'text-[#20A66A]',
    icon: <CheckCircle2 className="w-5 h-5 text-[#20A66A] shrink-0" />,
  },
  warning: {
    bg: 'bg-[#FFFDF5]',
    border: 'border-[#D4A923]/40',
    text: 'text-[#B88A18]',
    icon: <AlertTriangle className="w-5 h-5 text-[#D4A923] shrink-0" />,
  },
  error: {
    bg: 'bg-[#FDEDEF]',
    border: 'border-[#D94A5C]/40',
    text: 'text-[#D94A5C]',
    icon: <AlertCircle className="w-5 h-5 text-[#D94A5C] shrink-0" />,
  },
};

export const Alert: React.FC<AlertProps> = ({
  type = 'info',
  title,
  children,
  onClose,
  action,
  className = '',
}) => {
  const style = alertStyles[type];

  return (
    <div
      role="alert"
      className={`p-4 rounded-2xl border flex items-start gap-3.5 ${style.bg} ${style.border} ${className}`}
    >
      <div className="mt-0.5">{style.icon}</div>
      <div className="flex-1 text-xs sm:text-sm">
        {title && <h4 className={`font-bold mb-1 ${style.text}`}>{title}</h4>}
        <div className="text-[#111111] leading-relaxed">{children}</div>
        {action && <div className="mt-3">{action}</div>}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          aria-label="Dismiss alert"
          className="text-[#5F6368] hover:text-[#111111] p-1 transition-colors cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
